import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';

import { Declaration, DeclarationGoods, ProvidedDocuments } from '@e-origin/shared';

import { SendBatch } from '@features/batches';
import { isHighValueDeclaration } from '@utils';
import { getDuplicationModalTitle } from '../helpers';
import { DeclarationCustomsControl } from './DeclarationCustomsControl';
import { DeclarationDuplicationAndAmendment } from './DeclarationDuplicationAndAmendment';
import { DeclarationViewGoodHV, DeclarationViewGoodLV, GoodsItemUploadScreenshot } from './DeclarationView';
import { AmendmentModal } from './DeclarationView/DeclarationViewCustoms/Amendment/AmendmentModal';
import { GroupAmendmentModal } from './DeclarationView/DeclarationViewCustoms/Amendment/GroupAmendmentModal';
import { AddProvidedDocument } from './DeclarationView/DeclarationViewCustoms/ControlNotification/AddProvidedDocument';
import { ConfirmSendProvidedDocuments } from './DeclarationView/DeclarationViewCustoms/ControlNotification/ConfirmSendProvidedDocuments';
import { ViewGoodLVDocuments } from './DeclarationView/DeclarationViewGoodLV';
import { RiskAnalysis } from './RiskAnalysis';

export const declarationModalManager = {
  openDuplicateAndAmendment: (declaration: Declaration, isRfiAmendment?: boolean) => {
    modals.open({
      title: getDuplicationModalTitle(declaration, isRfiAmendment),
      size: 'lg',
      children: <DeclarationDuplicationAndAmendment declaration={declaration} isRfiAmendment={isRfiAmendment} />,
    });
  },
  openCustomsControl: (declaration: Declaration) => {
    modals.open({
      title: 'Customs Control',
      size: 'lg',
      children: <DeclarationCustomsControl declarationId={declaration._id} />,
    });
  },
  openSendBatch: (declaration: Declaration) => {
    modals.open({
      size: 'lg',
      withCloseButton: false,
      children: <SendBatch batchId={declaration.messageInfo.batch} title="Send declaration group" lrn={declaration.messageInfo.LRN} onHide={modals.closeAll} />,
    });
  },
  openGoodsItem: (declaration: Declaration, goodDetails: DeclarationGoods, disabled = false) => {
    const isHighValue = isHighValueDeclaration(declaration?.generalInfo?.group);
    const props = {
      onHide: () => modals.closeAll(),
      declaration,
      goodDetails,
      editDisabled: disabled,
    };
    modals.open({
      title: 'Goods Item',
      size: 'lg',
      children: isHighValue ? <DeclarationViewGoodHV {...props} /> : <DeclarationViewGoodLV {...props} />,
    });
  },
  openRiskAnalysis: (declarationId: string, goodDetails: DeclarationGoods) => {
    modals.open({
      title: 'Risk Analysis',
      size: '80vw',
      withCloseButton: false,
      children: <RiskAnalysis goodDetailsId={goodDetails?._id} onHide={modals.closeAll} declarationId={declarationId} />,
    });
  },
  openUploadScreenshot: (declaration: Declaration, goodDetails: DeclarationGoods) => {
    modals.open({
      title: 'Upload Screenshot',
      size: 'lg',
      children: (
        <GoodsItemUploadScreenshot
          opened
          declaration={declaration}
          onHide={() => modals.closeAll()}
          url={goodDetails?.webScraping?.webUrl || ''}
          goodDetailsId={goodDetails?._id || ''}
          afterCreate={() => void 0}
        />
      ),
    });
  },
  openDeleteDeclaration: (props: { declaration: Declaration; onConfirm: () => void }) => {
    modals.openConfirmModal({
      title: 'Confirm declaration delete',
      children: (
        <Text>
          Are you sure you want to delete the declaration? <br />
          This action cannot be undone!
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: props.onConfirm,
    });
  },
  openAddProvidedDocument: (declarationId: string) => {
    modals.open({
      title: 'Add Provided Document',
      size: 'lg',
      centered: true,
      children: <AddProvidedDocument declarationId={declarationId} onHide={modals.closeAll} />,
    });
  },
  openConfirmSendProvidedDocuments: (documents: ProvidedDocuments[], declarationId: string) => {
    modals.open({
      title: 'Are you sure you want to send the following documents?',
      size: 'xl',
      centered: true,
      children: <ConfirmSendProvidedDocuments documents={documents} declarationId={declarationId} onHide={modals.closeAll} />,
    });
  },
  openConfirmSendWithoutProvidedDocuments: (onConfirm: () => void) => {
    modals.openConfirmModal({
      title: 'This will inform customs that requested documents are not available. Are you sure?',
      labels: { confirm: 'Yes', cancel: 'No' },
      onConfirm,
    });
  },
  openDocumentsModal: (declaration: Declaration, goodDetails: DeclarationGoods, disabled = false) => {
    modals.open({
      title: 'Goods Shipment Item Documents',
      size: 'xl',
      centered: true,
      children: <ViewGoodLVDocuments declaration={declaration} goodDetails={goodDetails} editDisabled={disabled} />,
    });
  },
  openAmendmentModal: (declaration: Declaration) => {
    modals.open({
      title: 'This item amendments list',
      size: '80%',
      withCloseButton: true,
      children: <AmendmentModal declaration={declaration} />,
    });
  },
  openGroupAmendmentModal: (declaration: Declaration) => {
    modals.open({
      title: 'All items amendments',
      size: '80%',
      withCloseButton: true,
      children: <GroupAmendmentModal declaration={declaration} />,
    });
  },
};
